/**
 * Partner console (design-reference §4.14): a KPI strip, a per-tenant volume
 * overview, the tenants table and the create-tenant wizard. Reads the caller's
 * partner, its tenants and the cross-tenant overview from the real API.
 */
import { useMemo, useState } from 'react';

import { Button, DataTable, EmptyState, ErrorState, Skeleton, useToast, type Column } from '../../components';

import { useAdminApi } from './api';
import { CreateTenantWizard } from './CreateTenantWizard';
import { compact, firstWord, fmtInt, healthLevel, planLabel, statusLabel } from './format';
import { usePartnerConsole, type PartnerConsoleData, type TenantRow } from './useAdmin';

import './admin.css';

const HEALTH_LABEL: Record<string, string> = { ok: 'Healthy', warn: 'Attention', bad: 'Critical' };

function StatusPill({ row }: { row: TenantRow }): React.JSX.Element {
  const label = statusLabel(row.status, row.plan);
  const tone = row.status !== 'active' ? 'bad' : label === 'Trial' ? 'trial' : 'ok';
  return <span className={`adm-pill adm-pill--${tone}`}>{label}</span>;
}

function HealthDot({ level }: { level: string }): React.JSX.Element {
  const lvl = healthLevel(level);
  return (
    <span className="adm-health">
      <span className={`adm-dot adm-dot--${lvl}`} aria-hidden="true" />
      <span className="sr-only">{HEALTH_LABEL[lvl]}</span>
    </span>
  );
}

function VolumeByTenant({ rows }: { rows: TenantRow[] }): React.JSX.Element {
  const top = useMemo(
    () => [...rows].sort((a, b) => b.documents - a.documents).slice(0, 6),
    [rows],
  );
  const max = Math.max(1, ...top.map((r) => r.documents));
  return (
    <section className="adm-vbt" aria-label="Volume by tenant, last 30 days">
      <h3 className="adm-eyebrow">Volume by tenant · 30d</h3>
      <ul className="adm-vbt__list">
        {top.map((r) => {
          const width = Math.max(4, (r.documents / max) * 100);
          return (
            <li key={r.id} className="adm-vbt__row">
              <span className="adm-vbt__name" title={r.name}>
                {firstWord(r.name)}
              </span>
              <span className="adm-vbt__track">
                <span className="adm-vbt__fill" style={{ width: `${String(width)}%` }} />
              </span>
              <span className="adm-vbt__val">{compact(r.documents)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PartnerBody({ data, reload }: { data: PartnerConsoleData; reload: () => void }): React.JSX.Element {
  const { toast } = useToast();
  const api = useAdminApi();
  const [wizardOpen, setWizardOpen] = useState(false);

  const columns = useMemo<Column<TenantRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Tenant',
        render: (r) => <span className="adm-tenant-name">{r.name}</span>,
        sortValue: (r) => r.name.toLowerCase(),
      },
      { id: 'status', header: 'Status', render: (r) => <StatusPill row={r} /> },
      { id: 'plan', header: 'Plan', render: (r) => <span className="adm-muted">{planLabel(r.plan)}</span> },
      {
        id: 'volume',
        header: 'Volume · 30d',
        render: (r) => <span className="adm-num">{compact(r.documents)}</span>,
        sortValue: (r) => r.documents,
      },
      {
        id: 'backlog',
        header: 'Review backlog',
        render: (r) => (
          <span className={r.reviewBacklog > 20 ? 'adm-num adm-num--warn' : 'adm-num'}>
            {fmtInt(r.reviewBacklog)}
          </span>
        ),
        sortValue: (r) => r.reviewBacklog,
      },
      { id: 'health', header: 'Health', render: (r) => <HealthDot level={r.health} /> },
      {
        id: 'open',
        header: 'Open',
        render: (r) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              toast(`Context switch to ${r.name} — this elevation is audited`);
            }}
          >
            Open →
          </Button>
        ),
      },
    ],
    [toast],
  );

  const onCreated = (): void => {
    // The new tenant is live on the backend; refresh so the table and KPIs reflect
    // it. The wizard already raised the "tenant created" toast.
    reload();
  };

  return (
    <div className="adm-screen">
      <section className="adm-kpi-strip" aria-label="Partner summary">
        <div className="adm-kpi">
          <span className="adm-kpi__label">Tenants</span>
          <span className="adm-kpi__value">{fmtInt(data.totalTenants)}</span>
          <span className="adm-kpi__note">{data.partner?.name ?? 'No partner'}</span>
        </div>
        <div className="adm-kpi">
          <span className="adm-kpi__label">Documents · 30d</span>
          <span className="adm-kpi__value">{compact(data.totalDocuments)}</span>
          <span className="adm-kpi__note">across {fmtInt(data.rows.length)} tenants</span>
        </div>
        <div className="adm-kpi">
          <span className="adm-kpi__label">Needs attention</span>
          <span className="adm-kpi__value">{fmtInt(data.needsAttention)}</span>
          <span className="adm-kpi__note">backlog + suspension</span>
        </div>
        <VolumeByTenant rows={data.rows} />
      </section>

      <section className="adm-card" aria-labelledby="adm-tenants-h">
        <header className="adm-card__head">
          <div>
            <h2 id="adm-tenants-h" className="adm-card__title">
              Tenants
            </h2>
            <p className="adm-card__sub">{data.partner?.name ?? '—'}</p>
          </div>
          <Button
            onClick={() => {
              setWizardOpen(true);
            }}
            disabled={data.partner === null}
          >
            + Create tenant
          </Button>
        </header>

        {data.rows.length === 0 ? (
          <EmptyState
            title="No tenants yet"
            body="Create your first tenant — its base taxonomy is seeded automatically and the admin gets an SSO invite."
            actions={
              <Button
                onClick={() => {
                  setWizardOpen(true);
                }}
                disabled={data.partner === null}
              >
                + Create tenant
              </Button>
            }
          />
        ) : (
          <DataTable
            label="Tenants"
            columns={columns}
            rows={data.rows}
            getRowId={(r) => r.id}
            gridTemplate="1.6fr 1fr 0.9fr 1fr 1.1fr 0.8fr 90px"
            initialSort={{ columnId: 'volume', dir: -1 }}
          />
        )}

        <p className="adm-footnote">
          Opening a tenant switches context with an audited elevation — you act as partner admin inside
          their workspace.
        </p>
      </section>

      {data.partner !== null ? (
        <CreateTenantWizard
          open={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
          }}
          partnerId={data.partner.id}
          api={api}
          onCreated={onCreated}
        />
      ) : null}
    </div>
  );
}

export function PartnerScreen(): React.JSX.Element {
  const state = usePartnerConsole();

  if (state.status === 'loading') {
    return (
      <div className="adm-screen" aria-busy="true">
        <Skeleton height={92} />
        <Skeleton height={320} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="adm-screen">
        <ErrorState
          title="Couldn't load the partner console"
          body="The partner and tenant data failed to load."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  return <PartnerBody data={state.data} reload={state.reload} />;
}
